import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { UserPlus, Trash2, Settings, Shield, HardDrive, RefreshCw, Check, X, Plus, Edit2 } from 'lucide-react';

const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [cabins, setCabins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('employees');
  
  // New User Form
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'employee' });
  const [showUserModal, setShowUserModal] = useState(false);
  
  // Edit User Form
  const [editUser, setEditUser] = useState(null);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  
  // New Cabin Form
  const [newCabin, setNewCabin] = useState({ id: '', name: '' });
  const [showCabinModal, setShowCabinModal] = useState(false);
  
  // Extra minutes per cabin
  const [extraMinutes, setExtraMinutes] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, cabinsRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/cabins')
      ]);
      setUsers(usersRes.data);
      setCabins(cabinsRes.data);
    } catch (err) {
      console.error("Error fetching admin data", err);
    }
    setLoading(false);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/users', newUser);
      setNewUser({ name: '', email: '', password: '', role: 'employee' });
      setShowUserModal(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Error al crear usuario");
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      // Create a payload. Only send password if it's filled.
      const payload = { name: editUser.name, email: editUser.email, role: editUser.role };
      if (editUser.password) {
        payload.password = editUser.password;
      }
      await api.put(`/admin/users/${editUser.id}`, payload);
      setEditUser(null);
      setShowEditUserModal(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Error al actualizar usuario");
    }
  };

  const handleCreateCabin = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/cabins', newCabin);
      setNewCabin({ id: '', name: '' });
      setShowCabinModal(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Error al crear cabina");
    }
  };

  const handleDeleteCabin = async (id) => {
    if (!window.confirm("¿Seguro que quieres eliminar esta cabina? Las sesiones históricas se mantendrán, pero la cabina ya no aparecerá en el sistema.")) return;
    try {
      await api.delete(`/admin/cabins/${id}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Error al eliminar la cabina");
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("¿Seguro que quieres eliminar a este empleado?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleResetCabin = async (id) => {
    if (!window.confirm("¿Has cambiado los tubos? Esto pondrá a cero el contador de mantenimiento.")) return;
    try {
      await api.post(`/admin/cabins/${id}/reset`);
      fetchData();
    } catch (err) {
      alert("Error al resetear cabina");
    }
  };

  const handleAddMinutes = async (id) => {
    const mins = parseInt(extraMinutes[id]);
    if (!mins || mins === 0) return;
    try {
      await api.post(`/admin/cabins/${id}/add_minutes`, { minutes: mins });
      setExtraMinutes({ ...extraMinutes, [id]: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Error al añadir minutos");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-full text-stone-400">Cargando administración...</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <header className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-bold text-stone-800">Administración</h1>
          <p className="text-stone-500 mt-1">Gestión de personal y mantenimiento del centro</p>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex border-b border-stone-200">
        <button 
          onClick={() => setActiveTab('employees')}
          className={`px-6 py-3 font-medium transition-colors border-b-2 ${activeTab === 'employees' ? 'border-amber-500 text-amber-600' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
        >
          Empleados
        </button>
        <button 
          onClick={() => setActiveTab('cabins')}
          className={`px-6 py-3 font-medium transition-colors border-b-2 ${activeTab === 'cabins' ? 'border-amber-500 text-amber-600' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
        >
          Cabinas y Mantenimiento
        </button>
      </div>

      {activeTab === 'employees' ? (
        <section className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-stone-800 flex items-center gap-2">
              <Shield size={20} className="text-amber-500" />
              Personal Autorizado
            </h2>
            <button 
              onClick={() => setShowUserModal(true)}
              className="bg-stone-800 hover:bg-stone-900 text-white px-4 py-2 rounded-xl flex items-center gap-2 transition"
            >
              <UserPlus size={18} />
              Nuevo Empleado
            </button>
          </div>

          <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden shadow-sm">
            <table className="w-full text-left">
              <thead className="bg-stone-50 text-stone-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-4 font-semibold">Nombre</th>
                  <th className="px-6 py-4 font-semibold">Email</th>
                  <th className="px-6 py-4 font-semibold">Rol</th>
                  <th className="px-6 py-4 font-semibold">Creado el</th>
                  <th className="px-6 py-4 font-semibold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-stone-50/50 transition">
                    <td className="px-6 py-4 font-medium text-stone-800">{u.name}</td>
                    <td className="px-6 py-4 text-stone-600">{u.email}</td>
                    <td className="px-6 py-4 text-stone-600 lowercase">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${u.role === 'admin' ? 'bg-amber-100 text-amber-800' : 'bg-stone-100 text-stone-600'}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-stone-500 text-sm">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right flex justify-end gap-2">
                      <button 
                        onClick={() => {
                          setEditUser({ ...u, password: '' });
                          setShowEditUserModal(true);
                        }}
                        className="text-stone-400 hover:text-amber-500 transition"
                        title="Editar empleado"
                      >
                        <Edit2 size={18} />
                      </button>
                      {u.email !== 'admin@solplanet.com' && (
                        <button 
                          onClick={() => handleDeleteUser(u.id)}
                          className="text-stone-300 hover:text-red-500 transition"
                          title="Eliminar empleado"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <section className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-stone-800 flex items-center gap-2">
              <HardDrive size={20} className="text-amber-500" />
              Control de Vida de Cabinas
            </h2>
            <button 
              onClick={() => setShowCabinModal(true)}
              className="bg-stone-800 hover:bg-stone-900 text-white px-4 py-2 rounded-xl flex items-center gap-2 transition"
            >
              <Plus size={18} />
              Nueva Cabina
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cabins.map(c => {
               return (
                <div key={c.id} className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm space-y-4 flex flex-col justify-between">
                  <div>
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <h3 className="font-bold text-lg text-stone-800">{c.name}</h3>
                        <p className="text-stone-400 text-xs">ID: {c.id}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button 
                          onClick={() => handleResetCabin(c.id)}
                          className="p-2 text-stone-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition"
                          title="Resetear mantenimiento a 0"
                        >
                          <RefreshCw size={18} />
                        </button>
                        <button 
                          onClick={() => handleDeleteCabin(c.id)}
                          className="p-2 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                          title="Eliminar cabina"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1 mb-6">
                      <div className="text-xs font-medium uppercase tracking-wider text-stone-400">
                        Horas desde último cambio
                      </div>
                      <div className="text-3xl font-bold text-amber-600">
                        {Math.floor(c.maintenance_minutes / 60)}<span className="text-xl text-amber-600/70">h</span> {c.maintenance_minutes % 60}<span className="text-xl text-amber-600/70">m</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-stone-50 flex justify-between items-center">
                    <span className="text-xs text-stone-400 uppercase tracking-wider font-medium">Uso histórico total</span>
                    <span className="text-sm font-bold text-stone-700">
                      {Math.floor(c.total_minutes / 60)}h {c.total_minutes % 60}m
                    </span>
                  </div>

                  <div className="pt-4 border-t border-stone-100 flex items-center gap-2">
                    <input 
                      type="number" 
                      placeholder="Minutos extra..." 
                      className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500"
                      value={extraMinutes[c.id] || ''}
                      onChange={(e) => setExtraMinutes({...extraMinutes, [c.id]: e.target.value})}
                    />
                    <button 
                      onClick={() => handleAddMinutes(c.id)}
                      disabled={!extraMinutes[c.id] || parseInt(extraMinutes[c.id]) === 0}
                      className="bg-stone-800 hover:bg-stone-900 disabled:bg-stone-300 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors"
                      title="Añadir minutos sin cliente"
                    >
                      <Plus size={18} />
                    </button>
                  </div>
                </div>
               );
            })}
          </div>
        </section>
      )}

      {/* New User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={() => setShowUserModal(false)}></div>
          <div className="bg-white rounded-3xl w-full max-w-md relative z-10 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="bg-stone-50 px-8 py-6 border-b border-stone-100 flex justify-between items-center">
              <h3 className="text-xl font-bold text-stone-800 flex items-center gap-2">
                <UserPlus size={22} className="text-amber-500" />
                Registrar Personal
              </h3>
              <button onClick={() => setShowUserModal(false)} className="text-stone-400 hover:text-stone-600"><X /></button>
            </div>
            
            <form onSubmit={handleCreateUser} className="p-8 space-y-4">
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">Nombre Completo</label>
                <input 
                  type="text" 
                  required
                  value={newUser.name}
                  onChange={e => setNewUser({...newUser, name: e.target.value})}
                  className="w-full bg-stone-50 border-none rounded-xl px-4 py-3 text-stone-800 placeholder-stone-400 focus:ring-2 focus:ring-amber-500"
                  placeholder="Ej: Laura Martínez"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">Email / Usuario</label>
                <input 
                  type="email" 
                  required
                  value={newUser.email}
                  onChange={e => setNewUser({...newUser, email: e.target.value})}
                  className="w-full bg-stone-50 border-none rounded-xl px-4 py-3 text-stone-800 placeholder-stone-400 focus:ring-2 focus:ring-amber-500"
                  placeholder="laura@solplanet.com"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">Contraseña</label>
                <input 
                  type="password" 
                  required
                  value={newUser.password}
                  onChange={e => setNewUser({...newUser, password: e.target.value})}
                  className="w-full bg-stone-50 border-none rounded-xl px-4 py-3 text-stone-800 placeholder-stone-400 focus:ring-2 focus:ring-amber-500"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">Permisos</label>
                <select 
                  value={newUser.role}
                  onChange={e => setNewUser({...newUser, role: e.target.value})}
                  className="w-full bg-stone-50 border-none rounded-xl px-4 py-3 text-stone-800 focus:ring-2 focus:ring-amber-500"
                >
                  <option value="employee">Empleado (Acceso Básico)</option>
                  <option value="admin">Administrador (Acceso Total)</option>
                </select>
              </div>

              <div className="pt-4">
                <button 
                  type="submit"
                  className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900 font-bold py-4 rounded-2xl shadow-lg shadow-amber-500/20 transition-all active:scale-[0.98]"
                >
                  Confirmar Alta
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Cabin Modal */}
      {showCabinModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={() => setShowCabinModal(false)}></div>
          <div className="bg-white rounded-3xl w-full max-w-md relative z-10 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="bg-stone-50 px-8 py-6 border-b border-stone-100 flex justify-between items-center">
              <h3 className="text-xl font-bold text-stone-800 flex items-center gap-2">
                <Plus size={22} className="text-amber-500" />
                Registrar Nueva Cabina
              </h3>
              <button onClick={() => setShowCabinModal(false)} className="text-stone-400 hover:text-stone-600"><X /></button>
            </div>
            
            <form onSubmit={handleCreateCabin} className="p-8 space-y-4">
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">ID de la Cabina</label>
                <input 
                  type="text" 
                  required
                  value={newCabin.id}
                  onChange={e => setNewCabin({...newCabin, id: e.target.value})}
                  className="w-full bg-stone-50 border-none rounded-xl px-4 py-3 text-stone-800 placeholder-stone-400 focus:ring-2 focus:ring-amber-500"
                  placeholder="Ej: 6"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">Nombre Público</label>
                <input 
                  type="text" 
                  required
                  value={newCabin.name}
                  onChange={e => setNewCabin({...newCabin, name: e.target.value})}
                  className="w-full bg-stone-50 border-none rounded-xl px-4 py-3 text-stone-800 placeholder-stone-400 focus:ring-2 focus:ring-amber-500"
                  placeholder="Ej: Cabina 6"
                />
              </div>

              <div className="pt-4">
                <button 
                  type="submit"
                  className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900 font-bold py-4 rounded-2xl shadow-lg shadow-amber-500/20 transition-all active:scale-[0.98]"
                >
                  Confirmar Alta
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={() => setShowEditUserModal(false)}></div>
          <div className="bg-white rounded-3xl w-full max-w-md relative z-10 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="bg-stone-50 px-8 py-6 border-b border-stone-100 flex justify-between items-center">
              <h3 className="text-xl font-bold text-stone-800 flex items-center gap-2">
                <Edit2 size={22} className="text-amber-500" />
                Editar Personal
              </h3>
              <button onClick={() => setShowEditUserModal(false)} className="text-stone-400 hover:text-stone-600"><X /></button>
            </div>
            
            <form onSubmit={handleUpdateUser} className="p-8 space-y-4">
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">Nombre Completo</label>
                <input 
                  type="text" 
                  required
                  value={editUser.name}
                  onChange={e => setEditUser({...editUser, name: e.target.value})}
                  className="w-full bg-stone-50 border-none rounded-xl px-4 py-3 text-stone-800 placeholder-stone-400 focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">Email / Usuario</label>
                <input 
                  type="email" 
                  required
                  value={editUser.email}
                  onChange={e => setEditUser({...editUser, email: e.target.value})}
                  className="w-full bg-stone-50 border-none rounded-xl px-4 py-3 text-stone-800 placeholder-stone-400 focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">Nueva Contraseña (Opcional)</label>
                <input 
                  type="password" 
                  value={editUser.password}
                  onChange={e => setEditUser({...editUser, password: e.target.value})}
                  className="w-full bg-stone-50 border-none rounded-xl px-4 py-3 text-stone-800 placeholder-stone-400 focus:ring-2 focus:ring-amber-500"
                  placeholder="Dejar en blanco para no cambiar"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase tracking-wider mb-1.5">Permisos</label>
                <select 
                  value={editUser.role}
                  onChange={e => setEditUser({...editUser, role: e.target.value})}
                  className="w-full bg-stone-50 border-none rounded-xl px-4 py-3 text-stone-800 focus:ring-2 focus:ring-amber-500"
                  disabled={editUser.email === 'admin@solplanet.com'}
                >
                  <option value="employee">Empleado (Acceso Básico)</option>
                  <option value="admin">Administrador (Acceso Total)</option>
                </select>
                {editUser.email === 'admin@solplanet.com' && (
                  <p className="text-xs text-stone-400 mt-1">El rol del administrador principal no se puede cambiar.</p>
                )}
              </div>

              <div className="pt-4 flex gap-3">
                <button 
                  type="button"
                  onClick={() => setShowEditUserModal(false)}
                  className="flex-1 bg-stone-100 hover:bg-stone-200 text-stone-600 font-bold py-4 rounded-2xl transition-all"
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-stone-900 font-bold py-4 rounded-2xl shadow-lg shadow-amber-500/20 transition-all active:scale-[0.98]"
                >
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
